import React, { useState, useEffect } from "react";
import { FaTimes, FaDownload, FaEdit, FaTrash, FaExpand, FaShare, FaVideo } from "react-icons/fa";
import Carousel, { Modal, ModalGateway } from "react-images";
import ReactPlayer from 'react-player';
import ReelContent from '../pages/ReelContent';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';

const GeneratedImage = ({
  isGeneratingContent,
  contentGenerated,
  generatedImgContent,
  generatedVidContent,
  instagramPostType,
  timeRemaining,
  selectedImages,
  isEditingContent,
  editedContent,
  setEditedContent,
  isPlaying,
  setIsPlaying,
  isPublishing,
  publishStatus,
  contentTimestamp,
  viewerIsOpen,
  currentLightboxImage,
  creditInfo,
  user,
  handleStopGeneration,
  handleToggleEdit,
  handlePublish,
  handleScheduleLater,
  openLightbox,
  closeLightbox,
  handleClearContent,
  // New props for session-wide carousel
  generatedImgHistory = [],
  sessionSelectedImageIndex,
  setSessionSelectedImageIndex,
  // Product data props for video creation
  textboxUploadedImages = [],
  productType,
  productItem,
  selectedDresses = [],
  selectedHumanModel,
  selectedEvent,
  selectedStyle,
  realLooks,
  // Creation option prop
  selectedCreationOption
}) => {
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const [containerHeight, setContainerHeight] = useState(0);
  const navigate = useNavigate();
  const { getToken } = useAuth();

  // Calculate dynamic height based on viewport
  useEffect(() => {
    const calculateHeight = () => {
      const headerHeight = 64; // Approximate header height
      const chatboxHeight = window.innerWidth < 1024 ? 0 : 400; // Chatbox height on desktop
      const padding = 32; // Top and bottom padding
      const availableHeight = window.innerHeight - headerHeight - chatboxHeight - padding;
      setContainerHeight(Math.max(400, availableHeight)); // Minimum 400px
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);
    return () => window.removeEventListener('resize', calculateHeight);
  }, []);

  // Function to truncate text to first line
  const truncateText = (text) => {
    if (!text) return '';
    const firstLine = text.split('\n')[0];
    return firstLine.length > 100 ? firstLine.substring(0, 100) + '...' : firstLine;
  };

  // Function to check if text needs truncation
  const needsTruncation = (text) => {
    if (!text) return false;
    return text.includes('\n') || text.length > 100;
  };

  // Flatten history into a single array of images with content linkage
  const flattenedImages = Array.isArray(generatedImgHistory)
    ? generatedImgHistory.flatMap((entry) =>
        (entry.images || []).map((img) => ({
          ...img,
          __entryContent: entry.content || '',
          __entryTimestamp: entry.timestamp || ''
        }))
      )
    : [];

  // Fallback for older state (single batch) if no history provided
  const singleBatch = Array.isArray(flattenedImages) && flattenedImages.length === 0 && generatedImgContent && Array.isArray(generatedImgContent.images)
    ? generatedImgContent.images.map((img) => ({ ...img, __entryContent: generatedImgContent.content || '', __entryTimestamp: contentTimestamp || '' }))
    : [];

  const allImages = flattenedImages.length > 0 ? flattenedImages : singleBatch;
  const totalImages = allImages.length;

  const selectedIndex = typeof sessionSelectedImageIndex === 'number' && sessionSelectedImageIndex >= 0 && sessionSelectedImageIndex < totalImages
    ? sessionSelectedImageIndex
    : (totalImages > 0 ? totalImages - 1 : 0);

  const selectedItem = totalImages > 0 ? allImages[selectedIndex] : null;
  const selectedContentText = selectedItem ? (selectedItem.__entryContent || '') : (generatedImgContent?.content || '');

  const handlePrev = () => {
    if (totalImages === 0) return;
    const prevIdx = (selectedIndex - 1 + totalImages) % totalImages;
    setSessionSelectedImageIndex(prevIdx);
  };

  const handleNext = () => {
    if (totalImages === 0) return;
    const nextIdx = (selectedIndex + 1) % totalImages;
    setSessionSelectedImageIndex(nextIdx);
  };

  // Helper function to extract filename from URL
  const getFilenameFromUrl = (url, fallbackName) => {
    try {
      const pathname = new URL(url).pathname || '';
      const lastSegment = pathname.split('/').pop() || '';
      const cleaned = (lastSegment.split('?')[0] || lastSegment) || '';
      return decodeURIComponent(cleaned) || fallbackName;
    } catch (e) {
      return fallbackName;
    }
  };

  // Helper function to get file extension
  const getFileExtension = (filename, isVideo) => {
    const match = filename?.match(/\.([a-zA-Z0-9]+)(?:$|\?)/);
    let ext = match?.[1]?.toLowerCase();
    if (!ext) {
      ext = isVideo ? 'mp4' : 'png';
    }
    return ext;
  };

  // Helper function to infer MIME type
  const inferMimeType = (ext, isVideo) => {
    const imageMimeMap = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', gif: 'image/gif' };
    const videoMimeMap = { mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime' };
    return (isVideo ? videoMimeMap[ext] : imageMimeMap[ext]) || (isVideo ? 'video/mp4' : 'image/png');
  };

  // Helper function to download via anchor
  const downloadViaAnchor = (blob, filename) => {
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  };

  const handleDownload = async () => {
    try {
      if (!selectedItem || !selectedItem.url) {
        toast.error('No media to download');
        return;
      }

      const isVideo = selectedItem.isVideo || false;
      const mediaUrl = selectedItem.url;

      const fallbackName = `generated-content-${Date.now()}`;
      let filename = getFilenameFromUrl(mediaUrl, fallbackName);
      const ext = getFileExtension(filename, isVideo);
      if (!filename.toLowerCase().endsWith(`.${ext}`)) {
        filename = `${filename}.${ext}`;
      }

      // Check if mobile
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
        || (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);

      // Use backend proxy to avoid CORS and preserve auth
      const token = await getToken();
      const proxyUrl = `${process.env.REACT_APP_API_URL}/download_media?url=${encodeURIComponent(mediaUrl)}&filename=${encodeURIComponent(filename)}`;
      const response = await fetch(proxyUrl, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`Download failed (${response.status})`);
      const blob = await response.blob();
      const mimeType = blob.type || inferMimeType(ext, isVideo);
      const normalizedBlob = blob.type ? blob : new Blob([blob], { type: mimeType });

      // On mobile, use Web Share API to allow saving to gallery
      if (isMobile && navigator.share) {
        try {
          const file = new File([normalizedBlob], filename, { type: mimeType });
          const shareData = {
            title: isVideo ? 'Generated Video' : 'Generated Content',
            text: isVideo ? 'Download your generated video' : 'Download your generated content',
            files: [file]
          };
          
          // Check if files can be shared
          if (navigator.canShare && navigator.canShare(shareData)) {
            await navigator.share(shareData);
            return;
          }
        } catch (err) {
          // If share fails or is cancelled, fall through to download
          if (err.name === 'AbortError') {
            return; // User cancelled, don't show error
          }
          console.error('Share failed, falling back to download:', err);
        }
      }

      downloadViaAnchor(normalizedBlob, filename);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed. Please try again.');
    }
  };

  const handleMaximize = (e) => {
    const event = e || { preventDefault: () => {} };
    if (selectedItem?.url) {
      openLightbox(event, { photo: { src: selectedItem.url, index: selectedIndex }, index: selectedIndex });
    }
  };

  const handleShare = async () => {
    try {
      if (!selectedItem || !selectedItem.url) {
        toast.error('No media to share');
        return;
      }

      const isVideo = selectedItem.isVideo || false;
      const mediaUrl = selectedItem.url;

      // Check if Web Share API is supported
      if (navigator.share) {
        try {
          // Use backend proxy to avoid CORS and preserve auth
          const token = await getToken();
          const fallbackName = `generated-content-${Date.now()}`;
          let filename = getFilenameFromUrl(mediaUrl, fallbackName);
          const ext = getFileExtension(filename, isVideo);
          if (!filename.toLowerCase().endsWith(`.${ext}`)) {
            filename = `${filename}.${ext}`;
          }

          const proxyUrl = `${process.env.REACT_APP_API_URL}/download_media?url=${encodeURIComponent(mediaUrl)}&filename=${encodeURIComponent(filename)}`;
          const response = await fetch(proxyUrl, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` },
          });
          
          if (!response.ok) throw new Error(`Share failed (${response.status})`);
          
          const blob = await response.blob();
          const mimeType = blob.type || inferMimeType(ext, isVideo);
          const normalizedBlob = blob.type ? blob : new Blob([blob], { type: mimeType });
          const file = new File([normalizedBlob], filename, { type: mimeType });
          
          // Get content text if available
          const contentText = selectedContentText || '';
          
          const shareData = {
            title: isVideo ? 'Generated Video' : 'Generated Content',
            text: contentText ? contentText.substring(0, 100) : (isVideo ? 'Check out this cool AI video from Taco!' : 'Check out this cool AI image from Taco!'),
            files: [file]
          };

          // Check if files can be shared
          if (navigator.canShare && navigator.canShare(shareData)) {
            await navigator.share(shareData);
          } else {
            // Fallback to sharing just the URL if files aren't supported
            await navigator.share({
              title: isVideo ? 'Generated Video' : 'Generated Content',
              text: contentText ? contentText.substring(0, 100) : (isVideo ? 'Check out this cool AI video from Taco!' : 'Check out this cool AI image from Taco!'),
              url: mediaUrl
            });
          }
        } catch (err) {
          // User cancelled or error occurred
          if (err.name !== 'AbortError') {
            console.error('Error sharing:', err);
            toast.error('Share failed. Please try again.');
          }
        }
      } else {
        // Fallback for browsers that don't support Web Share API
        console.log('Web Share API not supported');
        toast.error('Sharing is not supported on this device');
      }
    } catch (error) {
      console.error('Share error:', error);
      toast.error('Share failed. Please try again.');
    }
  };

  // Create Video handler: stash image URL and product data, navigate to CreateVideo page
  const handleCreateVideo = () => {
    try {
      if (!selectedItem || !selectedItem.url) {
        toast.error('No image to use for video creation');
        return;
      }

      const mediaUrl = selectedItem.url;
      const filename = `video-reference-${Date.now()}.png`;
      
      // Store image URL and name in sessionStorage
      try {
        sessionStorage.setItem('pendingEditImageUrl', mediaUrl);
        sessionStorage.setItem('pendingEditImageName', filename);
      } catch (_) {}

      // Store product data in sessionStorage for video page
      try {
        const productData = {
          productType,
          productItem,
          selectedDresses,
          selectedHumanModel,
          selectedEvent,
          selectedStyle,
          realLooks,
          // Include textbox images data if available
          textboxImages: textboxUploadedImages.map(img => ({
            dataUrl: img.dataUrl,
            name: img.name,
            type: img.type,
            size: img.size,
            productType: img.productType,
            productItem: img.productItem,
            productView: img.productView,
            productCoverage: img.productCoverage,
            productCloseupDetail: img.productCloseupDetail,
            ProductImgDtl: img.ProductImgDtl
          }))
        };
        sessionStorage.setItem('pendingVideoProductData', JSON.stringify(productData));
      } catch (err) {
        console.error('Error storing product data:', err);
      }

      navigate('/video');
    } catch (e) {
      console.error('Create video navigation error:', e);
      toast.error('Unable to open video creator');
    }
  };

  // Credit info display as overlay
  const getCreditText = () => {
    if (!creditInfo) return null;
    
    const { total_cost, time_taken_sec } = creditInfo;
    
    if (typeof total_cost === 'number' || typeof time_taken_sec === 'number') {
      const costText = typeof total_cost === 'number' ? `$${total_cost.toFixed(4)}` : null;
      const timeText = typeof time_taken_sec === 'number' ? `${time_taken_sec.toFixed(2)}s` : null;
      return [costText, timeText].filter(Boolean).join(' • ');
    }
    
    const tokensRemaining = creditInfo.credit_left_tokens || creditInfo.credit_left || creditInfo.tokens || null;
    if (tokensRemaining !== null && tokensRemaining !== undefined && tokensRemaining !== '') {
      return `💎 ${tokensRemaining} tokens remaining`;
    }
    
    return null;
  };

  return (
    <>
      {/* Generated Content Section */}
      {contentGenerated && (generatedImgContent || generatedVidContent) && (
        <div 
          className="w-full h-full flex items-center justify-center lg:items-stretch lg:justify-stretch"
          style={{ height: `${containerHeight}px` }}
        >

            <div className="relative w-full h-full flex flex-col lg:flex-row gap-4 max-w-7xl lg:max-w-none mx-auto lg:mx-0 px-4 lg:px-0">
              
              {/* Main Image Display */}
              <div className="flex-1 relative bg-black/20 rounded-2xl overflow-hidden backdrop-blur-sm border border-gray-600/30">
                
                {/* Action Buttons Overlay */}
                <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
                  <button
                    onClick={handleShare}
                    className="w-10 h-10 bg-black/60 hover:bg-black/80 border border-gray-500/50 rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 backdrop-blur-sm lg:hidden"
                    title="Share"
                  >
                    <FaShare className="text-sm" />
                  </button>
                  <button
                    onClick={handleCreateVideo}
                    className="w-10 h-10 bg-black/60 hover:bg-black/80 border border-gray-500/50 rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 backdrop-blur-sm"
                    title="Create Video"
                  >
                    <FaVideo className="text-sm" />
                  </button>
                  <button
                    onClick={handleMaximize}
                    className="w-10 h-10 bg-black/60 hover:bg-black/80 border border-gray-500/50 rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 backdrop-blur-sm"
                    title="Maximize"
                  >
                    <FaExpand className="text-sm" />
                  </button>
                  <button
                    onClick={handleDownload}
                    className="w-10 h-10 bg-black/60 hover:bg-black/80 border border-gray-500/50 rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 backdrop-blur-sm"
                    title="Download"
                  >
                    <FaDownload className="text-sm" />
                  </button>
                  <button
                    onClick={selectedCreationOption === 'text_to_image' ? undefined : handleToggleEdit}
                    disabled={selectedCreationOption === 'text_to_image'}
                    className={`w-10 h-10 border border-gray-500/50 rounded-full flex items-center justify-center text-white transition-all duration-200 backdrop-blur-sm ${
                      selectedCreationOption === 'text_to_image' 
                        ? 'bg-gray-500/40 cursor-not-allowed opacity-50' 
                        : 'bg-black/60 hover:bg-black/80 hover:scale-110'
                    }`}
                    title={selectedCreationOption === 'text_to_image' ? 'Image edit is not possible for Text To Image option' : 'Edit'}
                  >
                    <FaEdit className="text-sm" />
                  </button>
                </div>

                {/* Navigation Buttons */}
                {totalImages > 1 && (
                  <>
                    <button
                      onClick={handlePrev}
                      className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 backdrop-blur-sm border border-gray-500/50"
                      title="Previous image"
                    >
                      ‹
                    </button>
                    <button
                      onClick={handleNext}
                      className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 backdrop-blur-sm border border-gray-500/50"
                      title="Next image"
                    >
                      ›
                    </button>
                  </>
                )}

                {/* Main Image */}
                <div className="w-full h-full flex items-center justify-center p-4">
                    <img
                      src={selectedItem?.url}
                      alt={`Generated content`}
                      className="w-full h-full object-contain cursor-pointer rounded-lg"
                      onClick={(event) => openLightbox(event, { photo: { src: selectedItem?.url, index: selectedIndex }, index: selectedIndex })}
                      style={{ maxHeight: '100%', maxWidth: '100%' }}
                    />
                </div>

                {/* Credit Info Overlay (like filename display) */}
                {/* {getCreditText() && (
                  <div className="absolute bottom-4 left-4 bg-black/60 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm border border-gray-500/50">
                    {getCreditText()}
                  </div>
                )} */}

                {/* Thumbnail Strip Overlay - Bottom */}
                {totalImages > 1 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {allImages.map((img, idx) => (
                      <button
                        key={`${img.url}-${idx}`}
                        className={`relative w-12 h-12 rounded-lg overflow-hidden border-2 transition-all duration-200 hover:scale-110 ${
                          idx === selectedIndex 
                            ? 'border-purple-400 ring-2 ring-purple-400/50' 
                            : 'border-gray-400/50 hover:border-gray-300'
                        }`}
                        onClick={() => setSessionSelectedImageIndex(idx)}
                        title={img.prompt || `Image ${idx + 1}`}
                      >
                        {img.isVideo ? (
                          <div className="w-full h-full bg-black">
                            <ReactPlayer 
                              url={img.url} 
                              width="100%" 
                              height="100%" 
                              playing={false} 
                              controls={false} 
                              playsinline={true} 
                              style={{ objectFit: 'cover' }} 
                            />
                          </div>
                        ) : (
                          <img 
                            src={img.url} 
                            alt={img.prompt || `thumb-${idx}`} 
                            className="w-full h-full object-cover" 
                          />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Content Panel - Right side on desktop, bottom on mobile */}
              <div className="w-full lg:w-80 p-6 flex flex-col">
                
              </div>
            </div>
          
        </div>
      )}

      {/* Lightbox Modal */}
      <ModalGateway>
        {viewerIsOpen ? (
          <Modal onClose={closeLightbox}>
            <Carousel
              currentIndex={currentLightboxImage}
              views={(allImages || []).map((image, index) => {
                // Store image properties in the closure to avoid reference issues
                const imageUrl = image.url;
                const isVideoFile = image.isVideo;
                const imagePrompt = image.prompt || `Generated content ${index + 1}`;
                
                return {
                  src: imageUrl,
                  caption: imagePrompt,
                  isVideo: isVideoFile,
                  render: () => (
                    isVideoFile ? (
                      <div className="w-full h-full bg-black flex items-center justify-center">
                        <ReactPlayer
                          url={imageUrl}
                          width="100%"
                          height="100%"
                          playing={true}
                          controls={true}
                          playsinline={true}
                          loop={true}
                          muted={false}
                          style={{ objectFit: "contain" }}
                          config={{
                            file: {
                              attributes: {
                                preload: 'auto',
                                controlsList: 'nodownload'
                              }
                            }
                          }}
                        />
                      </div>
                    ) : (
                      <img 
                        src={imageUrl} 
                        alt={imagePrompt}
                        className="max-w-full max-h-full object-contain"
                      />
                    )
                  )
                };
              })}
            />
          </Modal>
        ) : null}
      </ModalGateway>
    </>
  );
};

export default GeneratedImage; 